// kotActions.js
//
// Adapted from Waiter.js's mapActionsToItems/handleCollectiveReady — same
// GetresActionAllList row shape and the same Restaurant_Action_Update write
// model, just scoped down to a single order (the table/order selected on the
// Captain Dashboard) instead of every open order in the company.
//
// Four item states, spanning from a customer's QR order confirmation through
// to being served:
//   confirmed (statusId 10, "CONFIRMED BY CUSTOMER") — a QR order the
//                                              customer has placed and
//                                              confirmed, not yet sent to
//                                              the kitchen. Selectable in
//                                              the Pending Items modal, to be
//                                              dispatched to the kitchen.
//   preparing (statusId 11, "SENT TO KOT")  — kitchen has it. Not
//                                              interactive in the Kitchen
//                                              Items list, no checkbox.
//   prepared  (statusId 14, "READY")        — kitchen is done with it.
//                                              Checkbox enabled — this is
//                                              the only state a captain can
//                                              mark served from there.
//   served    (statusId 1999)               — captain already marked it
//                                              served. Checkbox disabled,
//                                              checked. Placeholder status,
//                                              not a real RESSTATUS row —
//                                              see API_MAPPING.md.

export const CONFIRMED_STATUS_ID = 10;
// KOT CONFIRMED BY CUSTOMER — a QR line waiting on the captain.
export const CONFIRMED_BY_CUSTOMER_STATUS_ID = 15;
export const PREPARING_STATUS_ID = 11;
export const PREPARED_STATUS_ID = 14;
export const SERVED_STATUS_ID = 1999;
// What "served" was written as before 1999; still read as served.
const LEGACY_SERVED_STATUS_ID = 999;
// The ORDER row's (actiontype 0) own CLOSED status — a different thing from
// an item being served, though it shares the old served number.
export const ORDER_CLOSED_STATUS_ID = 999;

const formatApiTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

// GetresActionAllList rows aren't guaranteed to echo back DeliveryDate in the
// same PascalCase the order screens (SpecialOrderDetails.js/RestaurantOrder.js/
// QrOrderDetails.js) POST it as — same casing inconsistency the requestId/
// menuId lookups elsewhere in this file already work around. Falls back to
// the row's own created date for rows written before this field existed.
const resolveDeliveryDate = (r) => {
    const value = r.DeliveryDate ?? r.deliveryDate ?? r.deliverydate;
    return value || r.createddate || null;
};

const statusFor = (statusId) => {
    const n = Number(statusId);
    if (n === SERVED_STATUS_ID || n === LEGACY_SERVED_STATUS_ID) return 'served';
    if (n === PREPARED_STATUS_ID) return 'prepared';
    if (n === CONFIRMED_STATUS_ID || n === CONFIRMED_BY_CUSTOMER_STATUS_ID) return 'confirmed';
    return 'preparing'; // 11, and anything else not yet handed off by the kitchen
};

// One GetresActionAllList row -> one KOT item, same field mapping Waiter.js uses.
export function mapActionsToItems(records, menuItems) {
    if (!Array.isArray(records)) return [];

    return records
        .filter(r => r.menuId !== null && r.menuId !== undefined)
        .map(r => {
            const status = statusFor(r.statusId);
            const menu = (menuItems || []).find(x =>
                String(x.value) === String(r.menuId) ||
                String(x.id) === String(r.menuId) ||
                String(x.menuId) === String(r.menuId)
            );

            return {
                ...r,
                id: r.requestactionid,
                requestId: r.requestid,
                kotNo: r.kotnumber,
                tableNo: r.tablenumber,
                tokenNo: r.tokennumber,
                createdTime: formatApiTime(r.createddate),
                deliveryDate: resolveDeliveryDate(r),
                name: menu ? (menu.label || menu.name || menu.menuName || menu.text) : `Menu ${r.menuId}`,
                qty: r.menuQty || 1,
                status,
                statusId: r.statusId,
                selected: status === 'served',
            };
        });
}

// Case-insensitive on purpose: requestId here can come from GetrestableOrderNum
// (a dine-in table's runningOrder) being matched against GetresActionAllList
// rows — two different endpoints whose GUIDs aren't guaranteed to share the
// same casing (RestaurantOrder.js already works around the same thing when
// comparing its own pendingRequestId). The Takeaway/Delivery dropdown path
// never hits this, since it builds its requestId straight from
// GetresActionAllList — same source, same casing, always matches.
export function itemsForRequest(items, requestId) {
    if (!requestId) return [];
    const target = String(requestId).toLowerCase();
    return items.filter(it => String(it.requestId).toLowerCase() === target);
}

// Strips the fields mapActionsToItems added, keeping the raw backend columns,
// and stamps the (placeholder) served status — same shape Waiter.js posts.
export function buildMarkServedPayload(selectedItems) {
    return selectedItems.map(item => {
        const {
            id, requestId, kotNo, tableNo, tokenNo, createdTime,
            name, qty, status, selected,
            ...backendData
        } = item;

        return {
            ...backendData,
            actiontype: 1,
            statusId: SERVED_STATUS_ID,
        };
    });
}

// Same technique, for dispatching a customer-confirmed QR item to the
// kitchen: statusId 10 (confirmed) -> 11 (sent to KOT).
export function buildSendToKotPayload(selectedItems) {
    return selectedItems.map(item => {
        const {
            id, requestId, kotNo, tableNo, tokenNo, createdTime,
            name, qty, status, selected,
            ...backendData
        } = item;

        return {
            ...backendData,
            actiontype: 1,
            statusId: PREPARING_STATUS_ID,
            printingrequired: '1',
        };
    });
}
